import { useState } from 'react'
import { PlanCard, PlanCardVariant } from '@/components/PlanCard'

const FPO_IMAGE = '/wp-content/plugins/ecs-spaces/public/images/spaces-fpo.png'

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-16'>
			<h3 className='spaces:text-14 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
				{title}
			</h3>
			{children}
		</div>
	)
}

const VARIANTS: PlanCardVariant[] = ['default', 'no-pricing', 'unavailable']

export function PlanCardSandbox () {
	const [variant, setVariant] = useState<PlanCardVariant>('default')

	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Variants ── */}
			<Section title='Variants'>
				<div className='spaces:flex spaces:gap-8 spaces:mb-8'>
					{VARIANTS.map(v => (
						<button
							key={v}
							onClick={() => setVariant(v)}
							className={`spaces:text-12 spaces:font-default spaces:rounded-8 spaces:px-12 spaces:py-8 spaces:transition-colors spaces:border spaces:border-canvas-3 ${
								variant === v
									? 'spaces:bg-brand-subtle spaces:text-brand-on-subtle spaces:border-brand-base'
									: 'spaces:text-content-2 spaces:hover:bg-canvas-2'
							}`}
						>
							{v}
						</button>
					))}
				</div>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16 spaces:items-start'>
					{/* hasFees = true */}
					<PlanCard
						planName='The Alpine'
						imageUrl={FPO_IMAGE}
						beds='2 Bed'
						baths='2 Bath'
						sqftRange='1,050–1,250'
						matchingUnits={6}
						totalPrice='$2,350'
						baseRent='$2,050'
						leaseTerm='12 Mo.'
						tags={[{ label: 'Special', color: 'success', iconLeft: 'Tag' }]}
						hasVirtualTour
						variant={variant}
						hasFees
						hasDisclaimerSymbol
					/>
					{/* hasFees = false */}
					<PlanCard
						planName='The Birch'
						imageUrl={FPO_IMAGE}
						beds='1 Bed'
						baths='1 Bath'
						sqftRange='650–800'
						matchingUnits={3}
						baseRent='$1,650'
						leaseTerm='12 Mo.'
						tags={[]}
						variant={variant}
						hasFees={false}
						hasDisclaimerSymbol={false}
					/>
				</div>
			</Section>

			{/* ── Pricing: with fees ── */}
			<Section title='Pricing: With Fees'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16 spaces:items-start'>
					<PlanCard
						planName='The Cedar'
						imageUrl={FPO_IMAGE}
						beds='3 Bed'
						baths='2 Bath'
						sqftRange='1,400–1,600'
						matchingUnits={2}
						totalPrice='$3,100'
						baseRent='$2,900'
						leaseTerm='12 Mo.'
						tags={[]}
						hasFees
						hasDisclaimerSymbol
					/>
					<PlanCard
						planName='The Dogwood'
						imageUrl={FPO_IMAGE}
						beds='2 Bed'
						baths='1 Bath'
						sqftRange='900–1,100'
						matchingUnits={4}
						totalPrice='$2,200'
						baseRent='$2,050'
						tags={[]}
						hasFees
						hasDisclaimerSymbol={false}
					/>
				</div>
			</Section>

			{/* ── Pricing: without fees ── */}
			<Section title='Pricing: Without Fees (Base Rent Only)'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16 spaces:items-start'>
					<PlanCard
						planName='The Elm'
						imageUrl={FPO_IMAGE}
						beds='1 Bed'
						baths='1 Bath'
						sqftRange='700–850'
						matchingUnits={8}
						baseRent='$1,800'
						leaseTerm='12 Mo.'
						tags={[]}
						hasFees={false}
						hasDisclaimerSymbol
					/>
					<PlanCard
						planName='The Fir'
						imageUrl={FPO_IMAGE}
						beds='Studio'
						baths='1 Bath'
						sqftRange='450–550'
						matchingUnits={5}
						baseRent='$1,350'
						tags={[]}
						hasFees={false}
						hasDisclaimerSymbol={false}
					/>
				</div>
			</Section>

			{/* ── Tags & Virtual Tour ── */}
			<Section title='Tags & Virtual Tour'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16 spaces:items-start'>
					<PlanCard
						planName='The Grove'
						imageUrl={FPO_IMAGE}
						beds='2 Bed'
						baths='2 Bath'
						sqftRange='1,050–1,150'
						matchingUnits={5}
						totalPrice='$2,500'
						baseRent='$2,200'
						leaseTerm='12 Mo.'
						tags={[
							{ label: 'Special', color: 'success', iconLeft: 'Tag' },
							{ label: 'New', color: 'brand' }
						]}
						hasVirtualTour
						hasFees
						hasDisclaimerSymbol={false}
					/>
					<PlanCard
						planName='The Hawthorne'
						imageUrl={FPO_IMAGE}
						beds='1 Bed'
						baths='1 Bath'
						sqftRange='700–800'
						matchingUnits={1}
						totalPrice='$1,750'
						baseRent='$1,600'
						leaseTerm='15 Mo.'
						tags={[{ label: 'Limited', color: 'warning' }]}
						hasFees
						hasDisclaimerSymbol={false}
					/>
					<PlanCard
						planName='The Ironwood'
						imageUrl={FPO_IMAGE}
						beds='3 Bed'
						baths='3 Bath'
						sqftRange='1,700–2,000'
						matchingUnits={0}
						totalPrice='$4,200'
						baseRent='$3,900'
						leaseTerm='12 Mo.'
						tags={[{ label: 'Penthouse', color: 'danger' }]}
						hasVirtualTour
						variant='unavailable'
						hasFees
						hasDisclaimerSymbol
					/>
				</div>
			</Section>

			{/* ── No image ── */}
			<Section title='No Image'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16 spaces:items-start'>
					<PlanCard
						planName='The Juniper'
						beds='1 Bed'
						baths='1 Bath'
						sqftRange='650–750'
						matchingUnits={4}
						totalPrice='$1,600'
						baseRent='$1,450'
						leaseTerm='12 Mo.'
						tags={[]}
						hasFees
						hasDisclaimerSymbol={false}
					/>
					<PlanCard
						planName='The Koa'
						beds='2 Bed'
						baths='2 Bath'
						sqftRange='1,000–1,150'
						matchingUnits={0}
						tags={[]}
						variant='no-pricing'
					/>
				</div>
			</Section>

			{/* ── Highlighted ── */}
			<Section title='Highlighted'>
				<div className='spaces:flex spaces:flex-wrap spaces:gap-16 spaces:items-start'>
					<PlanCard
						planName='The Larch'
						imageUrl={FPO_IMAGE}
						beds='2 Bed'
						baths='2 Bath'
						sqftRange='1,000–1,200'
						matchingUnits={3}
						totalPrice='$2,400'
						baseRent='$2,100'
						leaseTerm='12 Mo.'
						tags={[]}
						hasFees
						hasDisclaimerSymbol={false}
						highlighted={false}
					/>
					<PlanCard
						planName='The Maple'
						imageUrl={FPO_IMAGE}
						beds='2 Bed'
						baths='2 Bath'
						sqftRange='1,000–1,200'
						matchingUnits={3}
						totalPrice='$2,400'
						baseRent='$2,100'
						leaseTerm='12 Mo.'
						tags={[]}
						hasFees
						hasDisclaimerSymbol={false}
						highlighted
					/>
				</div>
			</Section>
		</div>
	)
}
